import { TagIcon } from "app/components/Icons";
import Pill from "app/components/Pill";
import CustomTooltip from "app/components/Tooltip";
import CommonTagSelection from "app/containers/Features/components/CommonTagSelection";
import { getTagsByIds } from "app/containers/Features/components/CommonTagSelection/helper";
import { ITags } from "app/containers/Features/components/CommonTagSelection/types";
import { selectTagList } from "app/redux/selector";
import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import { TicketTagsActionsEnum } from "../types";
import clsx from "clsx";
import { dropdownTriggerClassname } from "app/components/Dropdown";

interface TagProps {
  tagIds: string[];
  onUpdate: (tags: string[], action: TicketTagsActionsEnum) => void;
}

const TAG_PILL_CLASS = "!px-[6px] w-fit max-w-[84px] shrink-0";

const TagPill: React.FC<{ tag: ITags }> = ({ tag }) => (
  <CustomTooltip
    text={tag.title}
    position="bottom"
    className="shrink-0 max-w-[84px]"
  >
    <Pill
      className={TAG_PILL_CLASS}
      backgroundColor={tag.color}
      name={tag.title}
    />
  </CustomTooltip>
);

const TagSelection: React.FC<TagProps> = ({ tagIds, onUpdate }) => {
  const tagList = useSelector(selectTagList);

  const tagsObj = useMemo(() => {
    return getTagsByIds(tagList, tagIds);
  }, [tagList, tagIds]);

  const handleSelectionChange = (ids: string[]) => {
    const added = ids.filter((id) => !tagIds.includes(id));

    const action =
      added.length > 0
        ? TicketTagsActionsEnum.ADD
        : TicketTagsActionsEnum.REMOVE;

    onUpdate(ids, action);
  };

  const visibleTags = tagsObj?.slice(0, 2) ?? [];

  return (
    <div className="group flex flex-row items-center gap-1 shrink-0">
      <div className="flex flex-row items-center gap-1 shrink-0">
        {visibleTags.map((tag) => (
          <TagPill key={tag._id ?? tag.title} tag={tag} />
        ))}
        {tagsObj && tagsObj.length > 2 && (
          <CustomTooltip
            className="flex-shrink-0"
            position="bottom"
            contentClassName="max-w-[200px] flex flex-row flex-wrap gap-1"
            text={
              <div className="flex flex-row flex-wrap gap-1">
                {tagsObj.slice(2).map((tag) => (
                  <Pill
                    key={tag._id ?? tag.title}
                    className={TAG_PILL_CLASS}
                    backgroundColor={tag.color}
                    name={tag.title}
                  />
                ))}
              </div>
            }
          >
            <div className="flex items-center justify-center flex-shrink-0 h-[20px] min-w-[20px] px-[4px] bg-secondary rounded-full text-white font-medium text-[10px]">
              +{tagIds?.length - 2}
            </div>
          </CustomTooltip>
        )}
      </div>
      <CommonTagSelection
        trigger={
          <button
            className={clsx(
              "flex items-center justify-center flex-shrink-0 w-[20px] h-[20px] rounded-[2px] bg-white transition-colors opacity-0 group-hover:opacity-100 data-[state=open]:opacity-100 ",
              dropdownTriggerClassname,
            )}
          >
            <CustomTooltip
              text="Edit tags"
              className="w-full h-full flex items-center justify-center"
              position="bottom"
            >
              <TagIcon size={16} />
            </CustomTooltip>
          </button>
        }
        className="min-w-[255px] py-2"
        isHeader="Tags"
        selectedTags={tagIds}
        onTagsChange={handleSelectionChange}
      />
    </div>
  );
};

export default TagSelection;
